import { executeCommand } from 'owa-calendar-command-executor';
import type { CalendarEvent } from 'owa-calendar-types';
import type { ClientItemId } from 'owa-client-ids';

export type SurfaceMouseEvent = React.MouseEvent<HTMLDivElement>;
export type SurfaceKeyboardEvent = React.KeyboardEvent<HTMLDivElement>;

export type SurfaceUserEvent = SurfaceMouseEvent | SurfaceKeyboardEvent;

export type SurfaceUserEventHandler<TEvent> = (
    id: ClientItemId,
    parentFolderId: string,
    event: TEvent
) => void;

/**
 * Surface event handlers are default-bound to the CalendarEvent being clicked.
 *
 * See `owa-calendar-surface-view/lib/configs/OwaCalendarSurfaceEventHandlers.ts` for an example of this legacy
 * code pattern.
 *
 * This adapter simplifies the wrapping of surface event handlers to account for scenario-specific concerns, while
 * also removing the nuances of dealing with the bound-function architecture of the calendar surface controls.
 */
export default function SurfaceEventHandlerAdapter<TEvent extends SurfaceUserEvent>(
    handler: SurfaceUserEventHandler<TEvent>,
    isContextMenuHandler?: boolean,
    shouldStopPropagation?: (event: TEvent) => boolean
) {
    if (!handler) {
        return () => null;
    }

    return function (this: CalendarEvent, event: TEvent) {
        // preserve event reference so that downstream event handlers can attach to the correct element
        event.persist();
        const currentTarget: HTMLDivElement = event.currentTarget;

        if (isSurfaceMouseEvent(event)) {
            // ensure that surface event handler takes precendence over any surface handlers
            event.stopPropagation();

            // for context menu handler, also prevent standard browser context menu
            if (isContextMenuHandler) {
                event.preventDefault();
            }
        }

        if (shouldStopPropagation?.(event)) {
            event.preventDefault();
            event.stopPropagation();
        }

        // execute command after a single-tick delay to unify handling of multiple handlers
        executeCommand(() => {
            // in async scenario from executeCommand, current target gets lost, restore here
            event.currentTarget = currentTarget;
            handler(this.ItemId, this.ParentFolderId.Id, event);
        }, 0);
    };
}

function isSurfaceMouseEvent(event: SurfaceUserEvent): event is SurfaceMouseEvent {
    const mouseEvent = event as SurfaceMouseEvent;
    return (
        mouseEvent && typeof mouseEvent.pageX === 'number' && typeof mouseEvent.pageY === 'number'
    );
}
